import Header from "../components/Header";
import React, {useState} from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import {useDeleteRecallCard, useRecallCards, useSaveRecallCard, useRecallStats} from "./hooks/useRecall";
import {useAuth} from "../authentication/AuthContext";
import AddRecallCardModal from "./components/AddRecallCardModal";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import RecallSidebar from "./components/RecallSidebar";
import {useFolders} from "../folder/hooks/useFolder";

const RECALL_SCOPE = "RECALL";

export default function Recall() {
    const location = useLocation();
    const locationState = location.state ?? {};
    const [showModal, setShowModal] = useState(Boolean(locationState.openCreateModal));
    const [pageSize, setPageSize] = useState(25);
    const [currentPage, setCurrentPage] = useState(1);
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedCardIds, setSelectedCardIds] = useState(() => new Set());
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const {authToken, user} = useAuth();
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const activeFolder = searchParams.get("folder")
    const focusNoteId = searchParams.get("note") ?? locationState.focusNoteId ?? null
    const {data: recallCards = []} = useRecallCards(authToken, activeFolder, focusNoteId)
    const {data: stats} = useRecallStats(authToken, activeFolder)
    const dueCount = stats?.dueCount ?? 0
    const {data: folders = []} = useFolders(authToken, RECALL_SCOPE)
    const {mutate: saveCard} = useSaveRecallCard(authToken)
    const {mutateAsync: deleteCard, isLoading: isDeleting, error: deleteError, reset: resetDeleteError} = useDeleteRecallCard(authToken)
    const focusCardIds = React.useMemo(
        () => (searchParams.get("cards") ?? locationState.focusCardIds ?? "")
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean),
        [locationState.focusCardIds, searchParams]
    )
    const initialCard = locationState.initialCard ?? {}
    const folderNameById = React.useMemo(
        () => new Map(folders.map((folder) => [folder.id, folder.name])),
        [folders]
    )

    const displayCards = React.useMemo(
        () => recallCards.filter((c) => {
            return focusCardIds.length === 0 || focusCardIds.includes(String(c.id));
        }),
        [focusCardIds, recallCards]
    )
    const totalPages = Math.max(1, Math.ceil(displayCards.length / pageSize))
    const safeCurrentPage = Math.min(currentPage, totalPages)
    const pageStart = (safeCurrentPage - 1) * pageSize
    const paginatedCards = displayCards.slice(pageStart, pageStart + pageSize)
    const paginatedCardIds = paginatedCards.map((card) => String(card.id))
    const allPaginatedCardsSelected = paginatedCardIds.length > 0
        && paginatedCardIds.every((id) => selectedCardIds.has(id))

    React.useEffect(() => {
        setCurrentPage(1)
        setIsSelectionMode(false)
        setSelectedCardIds(new Set())
    }, [activeFolder, focusNoteId, focusCardIds])

    React.useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages)
        }
    }, [currentPage, totalPages])

    const reviewTarget = focusCardIds.length > 0 ? `?cards=${focusCardIds.join(",")}` : activeFolder ? `?folder=${activeFolder}` : "";

    const toggleCardSelection = (cardId) => {
        const normalizedId = String(cardId)
        setSelectedCardIds((current) => {
            const next = new Set(current)
            if (next.has(normalizedId)) {
                next.delete(normalizedId)
            } else {
                next.add(normalizedId)
            }
            return next
        })
    }

    const toggleCurrentPageSelection = () => {
        setSelectedCardIds((current) => {
            const next = new Set(current)
            if (allPaginatedCardsSelected) {
                paginatedCardIds.forEach((id) => next.delete(id))
            } else {
                paginatedCardIds.forEach((id) => next.add(id))
            }
            return next
        })
    }

    const toggleSelectionMode = () => {
        if (isDeleting) return

        setIsSelectionMode((current) => {
            if (current) setSelectedCardIds(new Set())
            return !current
        })
    }

    const handleDeleteSelected = async () => {
        if (selectedCardIds.size === 0 || isDeleting) return

        const idsToDelete = Array.from(selectedCardIds)
        try {
            await Promise.all(idsToDelete.map((cardId) => deleteCard(cardId)))
            setSelectedCardIds(new Set())
            setIsSelectionMode(false)
            setShowDeleteDialog(false)
        } catch {
            // The mutation exposes the error in the dialog and keeps the selection for retrying.
        }
    }

    const handleSubmit = (data) => {
        saveCard({
            userId: user?.uid,
            front: data.title,
            back: data.deadline,
            hint: data.repeat,
            tags: data.tags,
            parentFolderId: activeFolder ?? null,
            aiGenerated: false,
        });
        setShowModal(false);
    }

    return (
        <div className="flex min-h-screen flex-col bg-[var(--bg)]">
            <Header/>
            <div className="flex flex-1 gap-3 bg-[var(--workspace-bg)] px-3 pb-4 pt-[76px] md:gap-4 md:px-4">
                <RecallSidebar/>
                <main className="flex min-w-0 flex-1 flex-col overflow-y-auto">
                    {/* Toolbar */}
                    <div className="mt-8 mb-8 px-8 flex items-center justify-between gap-4">
                        <button
                                onClick={() => navigate(`/recall/review${reviewTarget}`)}
                                className="inline-flex items-center gap-2 rounded-xl bg-[var(--button-primary-bg)] px-6 py-3 text-sm font-semibold text-[var(--button-primary-text)] shadow-lg transition-all duration-200 hover:translate-y-[-1px] hover:brightness-105 active:translate-y-0 active:scale-[0.99]">
                            Start Review
                            {dueCount > 0 && (
                                <span className="ml-1 inline-flex items-center justify-center rounded-full bg-white/20 px-2 py-0.5 text-xs font-bold tabular-nums">
                                    {dueCount}
                                </span>
                            )}
                        </button>

                        <div className='flex gap-3 items-center'>
                            {isSelectionMode && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        resetDeleteError()
                                        setShowDeleteDialog(true)
                                    }}
                                    disabled={selectedCardIds.size === 0 || isDeleting}
                                    className="inline-flex items-center gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-300 shadow-sm shadow-black/20 transition hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    <DeleteOutlineIcon fontSize="small"/>
                                    {isDeleting ? "Deleting..." : `Delete${selectedCardIds.size > 0 ? ` (${selectedCardIds.size})` : ""}`}
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={toggleSelectionMode}
                                disabled={isDeleting}
                                aria-pressed={isSelectionMode}
                                className="inline-flex items-center rounded-md border border-[var(--border-color)] bg-[var(--surface-deep)] px-4 py-2 text-sm font-medium text-[var(--text-primary)] shadow-sm shadow-black/40 transition hover:bg-[var(--surface-hover)] disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                {isSelectionMode ? "Cancel" : "Select"}
                            </button>
                            <button
                                onClick={() => setShowModal(true)}
                                className="inline-flex items-center gap-2 rounded-md bg-[var(--surface-deep)] hover:bg-[var(--surface-hover)] active:bg-[var(--surface-hover)] px-4 py-2 text-sm font-medium text-[var(--text-primary)] border border-[var(--border-color)] shadow-sm shadow-black/40 transition"
                            >
                                <AddIcon fontSize="small"/>
                                New Card
                            </button>
                        </div>
                    </div>

                    {/* Stats */}
                    {stats && (
                        <div className="mt-6 mb-2 px-8 grid grid-cols-2 gap-3 sm:grid-cols-5">
                            <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-xl px-4 py-3">
                                <p className="text-xs text-[var(--text-muted)] mb-1">Total Cards</p>
                                <p className="text-xl font-bold text-[var(--text-primary)]">{stats.totalCards}</p>
                            </div>
                            <div className="bg-[var(--surface)] border border-[var(--brand-coral)]/20 rounded-xl px-4 py-3">
                                <p className="text-xs text-[var(--text-muted)] mb-1">Due Today</p>
                                <p className="text-xl font-bold text-[var(--brand-coral)]">{stats.dueCount}</p>
                            </div>
                            <div className="rounded-xl border border-[var(--brand-coral)]/20 bg-[var(--surface)] px-4 py-3">
                                <p className="text-xs text-[var(--text-muted)] mb-1">Reviewed</p>
                                <p className="text-xl font-bold text-[var(--brand-coral)]">{stats.reviewedCount}</p>
                            </div>
                            <div className="bg-[var(--surface)] border border-green-500/20 rounded-xl px-4 py-3">
                                <p className="text-xs text-[var(--text-muted)] mb-1">Avg. Success</p>
                                <p className="text-xl font-bold text-green-400">{Math.round(stats.avgSuccessRate * 100)}%</p>
                            </div>
                            <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-xl px-4 py-3">
                                <p className="text-xs text-[var(--text-muted)] mb-1">Correct / Wrong</p>
                                <p className="text-xl font-bold text-[var(--text-primary)]">
                                    <span className="text-green-400">{stats.totalCorrect}</span>
                                    <span className="text-[var(--text-muted)] mx-1">/</span>
                                    <span className="text-red-400">{stats.totalWrong}</span>
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Cards */}
                    <div className="flex flex-1 flex-col gap-2 px-8 pb-10">
                        {isSelectionMode && paginatedCards.length > 0 && (
                            <div className="flex items-center justify-between rounded-lg border border-[var(--border-color)] bg-[var(--surface)] px-4 py-2">
                                <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-[var(--text-muted)]">
                                    <input
                                        type="checkbox"
                                        checked={allPaginatedCardsSelected}
                                        onChange={toggleCurrentPageSelection}
                                        disabled={isDeleting}
                                        className="h-4 w-4 rounded border-[var(--border-strong)] bg-[var(--surface-deep)] text-[var(--brand-coral)] focus:ring-[var(--brand-coral)]"
                                    />
                                    Select this page
                                </label>
                                {selectedCardIds.size > 0 && (
                                    <span className="text-xs text-[var(--brand-coral)]">{selectedCardIds.size} selected</span>
                                )}
                            </div>
                        )}
                        {paginatedCards.map(recallCard => (
                            <div
                                key={recallCard.id}
                                className={`flex w-full items-center gap-3 rounded-xl border bg-[var(--surface-deep)] p-4 text-left transition-all hover:border-[var(--border-strong)] hover:bg-[var(--surface-hover)] ${selectedCardIds.has(String(recallCard.id)) ? "border-[var(--brand-coral)]/60 ring-1 ring-[var(--brand-coral)]/30" : "border-[var(--border-color)]"}`}
                            >
                                {isSelectionMode && (
                                    <input
                                        type="checkbox"
                                        checked={selectedCardIds.has(String(recallCard.id))}
                                        onChange={() => toggleCardSelection(recallCard.id)}
                                        disabled={isDeleting}
                                        aria-label={`Select ${recallCard.front}`}
                                        className="h-4 w-4 shrink-0 cursor-pointer rounded border-[var(--border-strong)] bg-[var(--surface-deep)] text-[var(--brand-coral)] focus:ring-[var(--brand-coral)]"
                                    />
                                )}
                                <button
                                    type="button"
                                    onClick={() => navigate(`/recall/${recallCard.id}`)}
                                    className="min-w-0 flex-1 text-left"
                                >
                                    <div className="flex justify-between items-center gap-4">
                                    <div className="min-w-0 flex-1">
                                        <h3 className="text-[var(--text-primary)] text-base font-semibold line-clamp-1">{recallCard.front}</h3>
                                        {!activeFolder && recallCard.parentFolderId && folderNameById.get(recallCard.parentFolderId) && (
                                            <span className="mt-2 inline-flex items-center gap-1 rounded-full border border-[var(--brand-coral)]/20 bg-[var(--brand-coral)]/10 px-2 py-0.5 text-[11px] font-medium text-[var(--brand-coral)]">
                                                <FolderOpenOutlinedIcon sx={{ fontSize: 12 }} />
                                                {folderNameById.get(recallCard.parentFolderId)}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap gap-2 text-xs text-[var(--text-secondary)]">
                                        {recallCard.tags?.split(",").map(tag => (
                                            <span key={tag} className="rounded-full border border-[var(--border-color)] bg-[var(--surface)] px-2 py-1">{tag.trim()}</span>
                                        ))}
                                    </div>
                                    </div>
                                </button>
                            </div>
                        ))}
                        {displayCards?.length === 0 && (
                            <p className="text-[var(--text-muted)] text-sm mt-8 text-center">No cards in this folder yet.</p>
                        )}
                        {displayCards.length > 0 && (
                            <div className="mt-auto flex flex-col gap-3 rounded-xl border border-[var(--border-color)] bg-[var(--surface)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                                    <span>Rows per page</span>
                                    <select
                                        value={pageSize}
                                        onChange={(event) => {
                                            setPageSize(Number(event.target.value))
                                            setCurrentPage(1)
                                        }}
                                        className="rounded-md border border-[var(--border-color)] bg-[var(--surface-deep)] px-2 py-1 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--brand-coral)]"
                                        aria-label="Recall cards per page"
                                    >
                                        {[25, 50, 100].map(size => (
                                            <option key={size} value={size}>{size}</option>
                                        ))}
                                    </select>
                                    <span>
                                        {pageStart + 1}–{Math.min(pageStart + pageSize, displayCards.length)} of {displayCards.length}
                                    </span>
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                                        disabled={safeCurrentPage === 1}
                                        className="rounded-md border border-[var(--border-color)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] transition hover:bg-[var(--surface-deep)] disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                        Previous
                                    </button>
                                    <span className="min-w-20 text-center text-xs text-[var(--text-muted)]">
                                        Page {safeCurrentPage} of {totalPages}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                                        disabled={safeCurrentPage === totalPages}
                                        className="rounded-md border border-[var(--border-color)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] transition hover:bg-[var(--surface-deep)] disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </main>
            </div>
            {showDeleteDialog && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 px-4 backdrop-blur-sm"
                    role="presentation"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget && !isDeleting) {
                            setShowDeleteDialog(false)
                        }
                    }}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="delete-recall-cards-title"
                        aria-describedby="delete-recall-cards-description"
                        className="w-full max-w-md rounded-2xl border border-[var(--border-color)] bg-[var(--surface)] p-6 shadow-2xl shadow-black/50"
                    >
                        <div className="flex items-start gap-4">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-red-500/30 bg-red-500/10 text-red-300">
                                <DeleteOutlineIcon/>
                            </div>
                            <div>
                                <h2 id="delete-recall-cards-title" className="text-lg font-semibold text-[var(--text-primary)]">
                                    Delete selected cards?
                                </h2>
                                <p id="delete-recall-cards-description" className="mt-2 text-sm leading-6 text-[var(--text-muted)]">
                                    {selectedCardIds.size} selected card{selectedCardIds.size === 1 ? "" : "s"} will be permanently deleted. This action cannot be undone.
                                </p>
                            </div>
                        </div>

                        {deleteError && (
                            <p className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                                Some selected cards could not be deleted. Please try again.
                            </p>
                        )}

                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setShowDeleteDialog(false)}
                                disabled={isDeleting}
                                className="rounded-lg border border-[var(--border-strong)] px-4 py-2 text-sm font-medium text-[var(--text-secondary)] transition hover:bg-[var(--surface-hover)] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleDeleteSelected}
                                disabled={isDeleting || selectedCardIds.size === 0}
                                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <DeleteOutlineIcon fontSize="small"/>
                                {isDeleting ? "Deleting..." : "Delete permanently"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showModal && (
                <AddRecallCardModal
                    onSubmit={handleSubmit}
                    onClose={() => setShowModal(false)}
                    initialFront={initialCard.title ?? ""}
                    initialTags={initialCard.tags ?? ""}
                />
            )}
        </div>
    )
}
